import { computed } from 'vue';
import store from '@/store';
import { UserRightsEnum } from '@/enums/global/UserRights';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { CommonUserGroups } from '@/enums/global/CommonUserGroups';

export function useUserRights() {
    const userRights = computed(() => store.getters['jwtData/userRights']);
    const userRoles = computed(() => store.getters['jwtData/userRoles']);
    const currentUserGroup = computed(() =>
        UserGroupRepository.getById(String(store.getters['jwtData/currentUserGroup'])),
    );

    function hasRight(userRight: UserRightsEnum) {
        return computed(() => userRights.value.includes(userRight));
    }

    const canCreateClients = hasRight(UserRightsEnum.CREATE_CLIENT);
    const canEditClients = hasRight(UserRightsEnum.EDIT_CLIENT);
    const canDeleteClients = hasRight(UserRightsEnum.DELETE_CLIENT);
    const canSeeInquiries = hasRight(UserRightsEnum.SEE_INQUIRIES);
    const canEditDataSegmentClient = hasRight(UserRightsEnum.EDIT_CLIENT_DETAILED);
    const canViewUsersTab = hasRight(UserRightsEnum.VIEW_USER);
    const canCreateUser = hasRight(UserRightsEnum.CREATE_USER);
    const canEditUser = hasRight(UserRightsEnum.EDIT_USER);
    const canEditUserRights = hasRight(UserRightsEnum.EDIT_USER_RIGHTS);
    const canSeeTranslationsItem = hasRight(UserRightsEnum.CMS_TRANSLATIONS);
    const canSeeCMSItem = hasRight(UserRightsEnum.CMS_CONTENT);
    const canSeeInfoButtonsItem = computed(() => true);
    const canSeeOrderPdfTemplatesItem = hasRight(UserRightsEnum.CMS_INFO_BUTTONS);
    const canSeeStatusesItem = hasRight(UserRightsEnum.CMS_STATUSES);
    const canSeeCampaignsItem = hasRight(UserRightsEnum.CMS_CAMPAIGNS);
    const canSeeCampaignTypesItem = hasRight(UserRightsEnum.CMS_CAMPAIGN_TYPES);
    const canSeeNextStepTypesItem = hasRight(UserRightsEnum.CMS_NEXT_STEP_TYPES);
    const canSeeClientTypesItem = hasRight(UserRightsEnum.CMS_CLIENT_TYPES);
    const canEditClientRestrictions = hasRight(UserRightsEnum.EDIT_CLIENT_RESTRICTIONS);
    const canSeeCatalogueItem = hasRight(UserRightsEnum.CMS_CATALOG);
    const canSeePostCodeConnectionsItem = hasRight(UserRightsEnum.CMS_POST_CODE_CONNECTIONS);
    const canSeeDocumentNamesItem = hasRight(UserRightsEnum.CMS_DOCUMENT_NAMES);
    const canSeeOfferConfigurationsItem = hasRight(UserRightsEnum.CMS_OFFER_CONFIGURATIONS);
    const canSeeTaxesItem = hasRight(UserRightsEnum.CMS_TAXES);
    const canSeeAutomaticPriceManipulations = hasRight(UserRightsEnum.CMS_AUTOMATIC_PRICE_MANIPULATIONS);
    const canSeePdfFlaggedValues = hasRight(UserRightsEnum.CMS_FLAGGED_FLAGGED_PDF_VALUES);
    const canSeeAdminTab = hasRight(UserRightsEnum.VIEW_LEAD_ASSIGN);
    const canSeeAllNextSteps = hasRight(UserRightsEnum.VIEW_ALL_NEXT_STEPS);
    const canEditNextSteps = hasRight(UserRightsEnum.EDIT_NEXT_STEPS);
    const canDeleteNextSteps = hasRight(UserRightsEnum.DELETE_NEXT_STEPS);
    const canSeeChat = hasRight(UserRightsEnum.VIEW_CHAT);
    const canUserDeleteOffers = hasRight(UserRightsEnum.DELETE_OFFER);
    const canSeeLeadTypesItem = hasRight(UserRightsEnum.CMS_LEAD_TYPES);
    const canSeeEmailTemplateProperties = hasRight(UserRightsEnum.CMS_EMAIL_TEMPLATE_PROPERTIES);
    const canSeeOrderStatusItem = hasRight(UserRightsEnum.CMS_ORDER_STATUSES);
    const canSeeLeadOriginsItem = hasRight(UserRightsEnum.CMS_LEAD_ORIGINS);
    const canSeeProductGroups = hasRight(UserRightsEnum.CMS_PRODUCT_GROUPS);
    const canUserSendOrders = hasRight(UserRightsEnum.SEND_ORDER);
    const canUserEditPurchasePriceManipulations = hasRight(UserRightsEnum.EDIT_PURCHASE_PRICE_MANIPULATIONS);
    const canUserSeeInquiries = hasRight(UserRightsEnum.SEE_INQUIRIES);
    const canAllowErrors = hasRight(UserRightsEnum.ALLOW_OFFER_ERRORS);
    const canPrintOfferSpecification = hasRight(UserRightsEnum.PRINT_OFFER_SPECIFICATION);
    const canSeeWarrantyCMS = hasRight(UserRightsEnum.CMS_VALID_WARRANTY);
    const canSeeRoltekMontage = hasRight(UserRightsEnum.VIEW_ROLTEK_MONTAGE);
    const canSeeProductVersions = hasRight(UserRightsEnum.VIEW_PRODUCT_VERSIONS);
    const canSendDataToIngenious = hasRight(UserRightsEnum.SEND_DATA_TO_INGENIOUS);
    const canCreateTechnologistOrder = hasRight(UserRightsEnum.CREATE_TECHNOLOGIST_ORDER);
    const canSetProjectStatusSold = hasRight(UserRightsEnum.SET_STATUS_TO_SOLD);
    const canSeeHolidayCalendar = hasRight(UserRightsEnum.CMS_HOLIDAY_CALENDAR);
    const canEditAccountingInfo = hasRight(UserRightsEnum.EDIT_CLIENT_ACCOUNTING_INFO);
    const canBlockClient = hasRight(UserRightsEnum.BLOCK_CLIENT);
    const canSeeDevTools = hasRight(UserRightsEnum.DEVELOPER);

    const isSuperAdmin = computed(() => userRoles.value.includes('ROLE_SUPER_ADMIN'));
    const canSeeClientStatusItem = computed(
        () => userRights.value.includes(UserRightsEnum.CMS_CLIENT_TYPES) && isSuperAdmin.value,
    );
    const canSeeUserGroupsItem = computed(() => isSuperAdmin.value);
    const canSeeCurrenciesItem = computed(
        () => isSuperAdmin.value && userRights.value.includes(UserRightsEnum.CMS_CURRENCIES),
    );
    const isCurrentUserGroupRoltek = computed(
        () =>
            currentUserGroup.value &&
            (Object as any).values(CommonUserGroups).includes(currentUserGroup.value.name),
    );
    const canSeeB2BControlPanel = computed(() => {
        if (currentUserGroup.value == null || currentUserGroup.value.name !== 'Roltek') {
            return false;
        }

        return userRights.value.includes(UserRightsEnum.B2B_CONTROL_PANEL);
    });
    const canSeeCalendar = computed(() => canSeeChat.value && canSeeAllNextSteps.value);
    const canAllowWarranty = computed(() => isSuperAdmin.value && canSeeWarrantyCMS.value);
    const canAccessLogistics = computed(() => {
        if (currentUserGroup.value == null || currentUserGroup.value.name !== 'Roltek') {
            return false;
        }

        return userRights.value.includes(UserRightsEnum.ACCESS_LOGISTICS);
    });

    return {
        canCreateClients,
        canEditClients,
        canDeleteClients,
        canSeeInquiries,
        canEditDataSegmentClient,
        canViewUsersTab,
        canCreateUser,
        canEditUser,
        canEditUserRights,
        canSeeTranslationsItem,
        canSeeCMSItem,
        canSeeInfoButtonsItem,
        canSeeOrderPdfTemplatesItem,
        canSeeStatusesItem,
        canSeeCampaignsItem,
        canSeeCampaignTypesItem,
        canSeeNextStepTypesItem,
        canSeeClientTypesItem,
        canSeeClientStatusItem,
        canEditClientRestrictions,
        canSeeCatalogueItem,
        canSeePostCodeConnectionsItem,
        canSeeDocumentNamesItem,
        canSeeOfferConfigurationsItem,
        canSeeTaxesItem,
        canSeeAutomaticPriceManipulations,
        canSeePdfFlaggedValues,
        canSeeAdminTab,
        canSeeAllNextSteps,
        canEditNextSteps,
        canDeleteNextSteps,
        canSeeChat,
        canUserDeleteOffers,
        isSuperAdmin,
        canSeeUserGroupsItem,
        canSeeCurrenciesItem,
        canSeeLeadTypesItem,
        canSeeEmailTemplateProperties,
        canSeeOrderStatusItem,
        canSeeLeadOriginsItem,
        canSeeProductGroups,
        canUserSendOrders,
        currentUserGroup,
        isCurrentUserGroupRoltek,
        canUserEditPurchasePriceManipulations,
        canUserSeeInquiries,
        canSeeB2BControlPanel,
        canSeeCalendar,
        userRights,
        canAllowErrors,
        canAllowWarranty,
        userRoles,
        canPrintOfferSpecification,
        canSeeWarrantyCMS,
        canSeeRoltekMontage,
        canSeeProductVersions,
        canSendDataToIngenious,
        canCreateTechnologistOrder,
        canSetProjectStatusSold,
        canAccessLogistics,
        canSeeHolidayCalendar,
        canEditAccountingInfo,
        canBlockClient,
        canSeeDevTools,
    };
}
